import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/feature/AdminLayout";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";

type Settings = {
  company_name: string;
  contact_email: string;
  contact_phone: string;
  timezone: string;
  currency: string;
  date_format: string;
  week_start: string;
  notify_new_booking: boolean;
  notify_new_project: boolean;
  notify_payment_received: boolean;
  notify_crew_clockin: boolean;
  weekly_summary: boolean;
};

const defaults: Settings = {
  company_name: "",
  contact_email: "",
  contact_phone: "",
  timezone: "America/Los_Angeles",
  currency: "USD",
  date_format: "MM/DD/YYYY",
  week_start: "monday",
  notify_new_booking: true,
  notify_new_project: true,
  notify_payment_received: true,
  notify_crew_clockin: false,
  weekly_summary: true,
};

const timezones = [
  { v: "America/Los_Angeles", label: "Pacific Time — Los Angeles" },
  { v: "America/Denver", label: "Mountain Time — Denver" },
  { v: "America/Chicago", label: "Central Time — Chicago" },
  { v: "America/New_York", label: "Eastern Time — New York" },
  { v: "Europe/London", label: "London (GMT)" },
  { v: "Europe/Berlin", label: "Berlin (CET)" },
];
const currencies = [
  { v: "USD", label: "USD — US Dollar" },
  { v: "CAD", label: "CAD — Canadian Dollar" },
  { v: "EUR", label: "EUR — Euro" },
  { v: "GBP", label: "GBP — British Pound" },
  { v: "AUD", label: "AUD — Australian Dollar" },
];
const dateFormats = [
  { v: "MM/DD/YYYY", label: "MM/DD/YYYY" },
  { v: "DD/MM/YYYY", label: "DD/MM/YYYY" },
  { v: "YYYY-MM-DD", label: "YYYY-MM-DD" },
];

type SectionKey = "general" | "notifications";
const sections: { key: SectionKey; icon: string; label: string; desc: string }[] = [
  {
    key: "general",
    icon: "ri-building-line",
    label: "General",
    desc: "Workspace, timezone, currency",
  },
  {
    key: "notifications",
    icon: "ri-notification-3-line",
    label: "Notifications",
    desc: "When the team gets pinged",
  },
];

export default function SettingsPage() {
  const navigate = useNavigate();
  useAuth();
  const [active, setActive] = useState<SectionKey>("general");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [data, setData] = useState<Settings>(defaults);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      const { data: row, error: e } = await supabase
        .from("workspace_settings")
        .select("*")
        .eq("id", 1)
        .maybeSingle();
      if (!alive) return;
      if (e) {
        setError(e.message);
      } else if (row) {
        setData({ ...defaults, ...row });
        setUpdatedAt(row.updated_at);
      }
      setLoading(false);
    };
    load();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  const update = <K extends keyof Settings>(key: K, value: Settings[K]) => {
    setData((d) => ({ ...d, [key]: value }));
  };

  const onSave = async () => {
    setSaving(true);
    setError("");
    const payload = { id: 1, ...data, updated_at: new Date().toISOString() };
    const { error: e } = await supabase
      .from("workspace_settings")
      .upsert(payload, { onConflict: "id" });
    setSaving(false);
    if (e) {
      setError(e.message);
      return;
    }
    setUpdatedAt(payload.updated_at);
    setToast("Settings saved");
  };

  return (
    <AdminLayout>
      <div className="px-4 md:px-8 py-6 max-w-6xl mx-auto">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-ink-500 mb-4">
          <button
            onClick={() => navigate("/dashboard")}
            className="hover:text-ink-900 cursor-pointer"
          >
            Dashboard
          </button>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-ink-900 font-semibold">Settings</span>
        </nav>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-ink-900">
              Settings
            </h1>
            <p className="text-sm text-ink-500 mt-1">
              Configure the basics for your Haulmark workspace.
            </p>
          </div>
          <div className="text-xs text-ink-500">
            {updatedAt ? (
              <>
                Last saved{" "}
                <span className="font-semibold text-ink-700">
                  {new Date(updatedAt).toLocaleString()}
                </span>
              </>
            ) : (
              <span>Not saved yet</span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4">
          {/* Sidebar nav */}
          <aside className="bg-white border border-ink-100 rounded-lg p-2 h-fit">
            {sections.map((s) => {
              const isActive = active === s.key;
              return (
                <button
                  key={s.key}
                  onClick={() => setActive(s.key)}
                  className={`w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-md cursor-pointer transition-colors ${
                    isActive
                      ? "bg-ink-900 text-white"
                      : "text-ink-700 hover:bg-cream-100"
                  }`}
                >
                  <span
                    className={`w-8 h-8 flex items-center justify-center rounded-md shrink-0 ${
                      isActive ? "bg-white/10" : "bg-cream-100 text-ink-700"
                    }`}
                  >
                    <i className={`${s.icon} text-base`}></i>
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold">{s.label}</div>
                    <div
                      className={`text-[11px] mt-0.5 truncate ${
                        isActive ? "text-white/60" : "text-ink-500"
                      }`}
                    >
                      {s.desc}
                    </div>
                  </div>
                </button>
              );
            })}
          </aside>

          {/* Content */}
          <div className="min-w-0">
            {loading ? (
              <div className="bg-white border border-ink-100 rounded-lg p-10 text-center text-sm text-ink-500">
                <i className="ri-loader-4-line animate-spin mr-2"></i>
                Loading settings…
              </div>
            ) : (
              <>
                {active === "general" && (
                  <Card
                    title="Company info"
                    desc="Used across invoices, emails, and the customer-facing site."
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label="Company name">
                        <input
                          type="text"
                          className="form-input"
                          value={data.company_name}
                          onChange={(e) => update("company_name", e.target.value)}
                          placeholder="Haulmark Movers"
                        />
                      </Field>
                      <Field label="Contact email">
                        <input
                          type="email"
                          className="form-input"
                          value={data.contact_email}
                          onChange={(e) => update("contact_email", e.target.value)}
                          placeholder="hello@haulmark.co"
                        />
                      </Field>
                      <Field label="Contact phone">
                        <input
                          type="text"
                          className="form-input"
                          value={data.contact_phone}
                          onChange={(e) => update("contact_phone", e.target.value)}
                          placeholder="+1 (415) 555-0136"
                        />
                      </Field>
                      <Field label="Timezone">
                        <select
                          className="form-input"
                          value={data.timezone}
                          onChange={(e) => update("timezone", e.target.value)}
                        >
                          {timezones.map((t) => (
                            <option key={t.v} value={t.v}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Currency">
                        <select
                          className="form-input"
                          value={data.currency}
                          onChange={(e) => update("currency", e.target.value)}
                        >
                          {currencies.map((c) => (
                            <option key={c.v} value={c.v}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Date format">
                        <select
                          className="form-input"
                          value={data.date_format}
                          onChange={(e) => update("date_format", e.target.value)}
                        >
                          {dateFormats.map((d) => (
                            <option key={d.v} value={d.v}>
                              {d.label}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </div>

                    <div className="mt-5">
                      <label className="block text-xs font-bold uppercase tracking-wider text-ink-500 mb-2">
                        Week starts on
                      </label>
                      <div className="inline-flex p-1 bg-cream-100 rounded-full">
                        {["sunday", "monday"].map((d) => {
                          const isActive = data.week_start === d;
                          return (
                            <button
                              key={d}
                              onClick={() => update("week_start", d)}
                              className={`px-4 py-1.5 rounded-full text-xs font-semibold capitalize cursor-pointer whitespace-nowrap transition-colors ${
                                isActive
                                  ? "bg-white text-ink-900"
                                  : "text-ink-500 hover:text-ink-900"
                              }`}
                            >
                              {d}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <FormFooter
                      saving={saving}
                      error={error}
                      onSave={onSave}
                    />
                  </Card>
                )}

                {active === "notifications" && (
                  <Card
                    title="Notifications"
                    desc="Choose which events should ping the team."
                  >
                    <div className="divide-y divide-ink-100">
                      <Toggle
                        icon="ri-calendar-event-line"
                        title="New booking received"
                        desc="Ping when a customer books a new move."
                        value={data.notify_new_booking}
                        onChange={(v) => update("notify_new_booking", v)}
                      />
                      <Toggle
                        icon="ri-truck-line"
                        title="New project created"
                        desc="Notify dispatch when a new project is added."
                        value={data.notify_new_project}
                        onChange={(v) => update("notify_new_project", v)}
                      />
                      <Toggle
                        icon="ri-bank-card-line"
                        title="Payment received"
                        desc="Get notified when payments clear."
                        value={data.notify_payment_received}
                        onChange={(v) => update("notify_payment_received", v)}
                      />
                      <Toggle
                        icon="ri-time-line"
                        title="Crew clock-in / out"
                        desc="Real-time pings when crew start or end shifts."
                        value={data.notify_crew_clockin}
                        onChange={(v) => update("notify_crew_clockin", v)}
                      />
                      <Toggle
                        icon="ri-mail-line"
                        title="Weekly summary email"
                        desc="Friday digest with revenue, bookings & feedback."
                        value={data.weekly_summary}
                        onChange={(v) => update("weekly_summary", v)}
                      />
                    </div>

                    <FormFooter
                      saving={saving}
                      error={error}
                      onSave={onSave}
                    />
                  </Card>
                )}

              </>
            )}
          </div>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-ink-900 text-white text-sm font-semibold px-4 py-3 rounded-md flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-emerald-400"></i>
          {toast}
        </div>
      )}
    </AdminLayout>
  );
}

function Card({
  title,
  desc,
  children,
}: {
  title: string;
  desc?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white border border-ink-100 rounded-lg p-6">
      <div className="mb-5">
        <h2 className="font-display text-lg font-bold text-ink-900">{title}</h2>
        {desc && <p className="text-xs text-ink-500 mt-0.5">{desc}</p>}
      </div>
      {children}
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wider text-ink-500 mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}

function FormFooter({
  saving,
  error,
  onSave,
}: {
  saving: boolean;
  error: string;
  onSave: () => void;
}) {
  return (
    <>
      {error && (
        <div className="mt-4 flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2 rounded-md">
          <i className="ri-error-warning-line mt-0.5"></i>
          <span>{error}</span>
        </div>
      )}
      <div className="mt-6 pt-5 border-t border-ink-100 flex items-center justify-end gap-2">
        <button
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold bg-ink-900 hover:bg-ink-800 text-white cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              Saving…
            </>
          ) : (
            <>
              <i className="ri-save-line"></i>
              Save changes
            </>
          )}
        </button>
      </div>
    </>
  );
}

function Toggle({
  icon,
  title,
  desc,
  value,
  onChange,
}: {
  icon: string;
  title: string;
  desc: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="py-4 flex items-start justify-between gap-4">
      <div className="flex items-start gap-3 min-w-0">
        <span className="w-9 h-9 flex items-center justify-center rounded-md bg-cream-100 text-ink-700 shrink-0">
          <i className={`${icon} text-base`}></i>
        </span>
        <div className="min-w-0">
          <div className="text-sm font-bold text-ink-900">{title}</div>
          <div className="text-xs text-ink-500 mt-0.5">{desc}</div>
        </div>
      </div>
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`shrink-0 relative w-11 h-6 rounded-full transition-colors cursor-pointer ${
          value ? "bg-ink-900" : "bg-ink-200"
        }`}
        aria-pressed={value}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
            value ? "translate-x-5" : "translate-x-0"
          }`}
        ></span>
      </button>
    </div>
  );
}

